import { useEffect, useState } from "react";
import clinicAPI from "../../../services/clinicApi";
import { HubFormCard } from "../AdminPortal/HubPwa";
import aa from "../AdminPortal/adminApp.module.css";
import s from "../clinic.module.css";
import L from "../../../constants/baysportLabels";
import { Button, Card, EmptyState, Field, Input, Select, useToast } from "../../ui";

function qrImageUrl(text) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(text)}`;
}

function formatExpiry(iso) {
  if (!iso) return "Без срока";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function RegistrationLinksPanel({ compact = false }) {
  const toast = useToast();
  const [links, setLinks] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: "Набор в группу",
    specialization_id: "",
    expires_at: "",
    max_uses: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [linksRes, groupsRes] = await Promise.all([
        clinicAPI.listRegistrationLinks(),
        clinicAPI.getSportGroupsSummary().catch(() =>
          clinicAPI.getServicesCatalog().then((c) => ({
            groups: c.specializations || [],
          })),
        ),
      ]);
      setLinks(linksRes.links || []);
      setGroups(groupsRes.groups || groupsRes.specializations || []);
    } catch (e) {
      toast.error(e.response?.data?.error || "Не удалось загрузить ссылки");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const createLink = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const r = await clinicAPI.createRegistrationLink({
        title: form.title.trim() || "Регистрация",
        specialization_id: form.specialization_id || null,
        expires_at: form.expires_at || null,
        max_uses: form.max_uses ? Number(form.max_uses) : null,
      });
      setLinks((prev) => [r.link, ...prev]);
      setForm((f) => ({ ...f, title: "Набор в группу", expires_at: "", max_uses: "" }));
      toast.success("Ссылка создана");
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка создания");
    } finally {
      setCreating(false);
    }
  };

  const copyUrl = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Ссылка скопирована");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  const toggleActive = async (link) => {
    try {
      const r = await clinicAPI.updateRegistrationLink(link.id, {
        is_active: !link.is_active,
      });
      setLinks((prev) => prev.map((l) => (l.id === link.id ? r.link : l)));
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const removeLink = async (link) => {
    if (!window.confirm(`Удалить ссылку «${link.title}»?`)) return;
    try {
      await clinicAPI.deleteRegistrationLink(link.id);
      setLinks((prev) => prev.filter((l) => l.id !== link.id));
      toast.success("Удалено");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка удаления");
    }
  };

  const formBlock = (
    <form onSubmit={createLink} className={s.clientTabFormStack}>
      <Field label="Название ссылки">
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Набор U12, летний лагерь…"
        />
      </Field>
      <Field label={L.group}>
        <Select
          value={form.specialization_id}
          onChange={(e) =>
            setForm({ ...form, specialization_id: e.target.value })
          }
        >
          <option value="">Без привязки к группе</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Срок действия" hint="Пусто — без ограничения">
        <Input
          type="datetime-local"
          value={form.expires_at}
          onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
        />
      </Field>
      <Field label="Лимит заявок" hint="Пусто — без лимита">
        <Input
          type="number"
          min={1}
          value={form.max_uses}
          onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
          placeholder="20"
        />
      </Field>
      <Button type="submit" disabled={creating}>
        {creating ? "Создание…" : "Создать ссылку"}
      </Button>
    </form>
  );

  const listBlock =
    loading ? (
      <p className={aa.hubHint}>Загрузка…</p>
    ) : links.length === 0 ? (
      <EmptyState title="Пока нет ссылок" hint="Создайте ссылку для публичной регистрации" />
    ) : (
      <div className={aa.hubListGroup}>
        {links.map((link) => (
          <div key={link.id} className={s.registrationLinkCard}>
            <div className={s.registrationLinkHead}>
              <strong>{link.title}</strong>
              <span
                className={
                  link.is_usable ? s.registrationLinkStatusOn : s.registrationLinkStatusOff
                }
              >
                {link.is_usable ? "Активна" : "Недоступна"}
              </span>
            </div>
            {link.group ? (
              <p className={aa.hubHint}>Группа: {link.group.name}</p>
            ) : null}
            <p className={aa.hubHint}>
              Заявок: {link.use_count}
              {link.max_uses != null ? ` / ${link.max_uses}` : ""} · до{" "}
              {formatExpiry(link.expires_at)}
            </p>
            {link.url ? (
              <div className={s.registrationLinkUrlRow}>
                <Input value={link.url} readOnly />
                <Button type="button" variant="secondary" onClick={() => copyUrl(link.url)}>
                  Копировать
                </Button>
              </div>
            ) : null}
            {link.url && link.is_usable ? (
              <div className={s.registrationLinkQr}>
                <img src={qrImageUrl(link.url)} alt="" width={120} height={120} />
                <span className={aa.hubHint}>QR для стены и соцсетей</span>
              </div>
            ) : null}
            <div className={s.registrationLinkActions}>
              <Button type="button" size="sm" variant="secondary" onClick={() => toggleActive(link)}>
                {link.is_active ? "Отключить" : "Включить"}
              </Button>
              <Button type="button" size="sm" variant="secondary" onClick={() => removeLink(link)}>
                Удалить
              </Button>
            </div>
          </div>
        ))}
      </div>
    );

  if (compact) {
    return (
      <>
        <HubFormCard title="Новая ссылка">{formBlock}</HubFormCard>
        {listBlock}
      </>
    );
  }

  return (
    <div className={s.settingsPanel}>
      <p className={s.docSectionHint}>
        Публичные ссылки для самостоятельной регистрации родителей. Заявки попадают в
        раздел «На модерации» — после подтверждения родитель получит приглашение задать
        пароль.
      </p>
      <div className={s.registrationLinksGrid}>
        <Card title="Создать ссылку">{formBlock}</Card>
        <Card title="Активные ссылки">{listBlock}</Card>
      </div>
    </div>
  );
}
